import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrap } from '../lib/api-envelope';

/**
 * GET /field/my-work (api#972) — the caller's assigned work across their
 * tenant's verticals, in one shape. The API decides every status, label and
 * count; nothing here recomputes one.
 *
 * Casing screens do NOT read this. They keep their own GET /jobs and
 * /employee/dashboard requests exactly as they were; this hook is called only
 * behind a positive guard for a non-casing vertical, via `enabled`.
 */
export interface MyWorkItem {
  vertical: string;
  id: string;
  number: string | null;
  title: string | null;
  date: string | null;
  isToday: boolean;
  status: string;
  statusLabel: string;
  active: boolean;
  linkable: boolean;
}

export interface MyWorkSummary {
  vertical: string;
  total: number;
  active: number;
  upcoming: number;
}

export interface MyWorkResponse {
  today: string;
  timezone: string;
  verticals: string[];
  items: MyWorkItem[];
  summary: MyWorkSummary[];
}

export type MyWorkScope = 'open' | 'all';

export function useMyWork(scope: MyWorkScope, options: { enabled: boolean }) {
  return useQuery({
    queryKey: ['field-my-work', scope],
    queryFn: async () => unwrap<MyWorkResponse>((await api.field.myWork(scope)).data),
    enabled: options.enabled,
    staleTime: 30_000,
  });
}
